import React, { useState, useEffect, useRef } from 'react';
import { Send, X, MapPin } from 'lucide-react';
import { animate, createTimeline } from 'animejs';
import { createPortal } from 'react-dom';
import TrackingMap from '../maps/TrackingMap';
import PositionMap from '../maps/PositionMap';
import ServiceTimeline, { ServiceTimelineSkeleton } from './ServiceTimeline';
import { api_ia } from '../../config/config';
import "./chatbot.css"
import skybotLogoWebp from './skybot-logo.webp';

/* ── SkyAngel Robot Logo ────────────────────────────── */
const TfLogo = ({ size = 34, animated = true }) => (
  <img
    src={skybotLogoWebp}
    alt="SkyAngel"
    draggable={false}
    className={`skybot-logo-img${animated ? ' skybot-logo-anim' : ''}`}
    style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0, pointerEvents: 'none' }}
  />
);

/* ── Helpers ──────────────────────────────────────────── */
const MESES_ES = {
  enero:1, febrero:2, marzo:3, abril:4, mayo:5, junio:6,
  julio:7, agosto:8, septiembre:9, octubre:10, noviembre:11, diciembre:12
};

const detectETA = (text) => {
  if (!text) return null;

  // Formato ISO: "ETA: 2026-05-30 a las 17:13:27"
  const etaIso = text.match(
    /\bETA\b[^0-9]*([0-9]{4}-[0-9]{2}-[0-9]{2})(?:\s+a\s+las\s+|\s+|T)([0-9]{2}:[0-9]{2}(?::[0-9]{2})?)/i
  );

  // Formato español: "ETA Estimado: 30 de mayo de 2026 a las 17:13:27"
  const etaEs = text.match(
    /\bETA\b[^0-9]*(\d{1,2})\s+de\s+(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre)\s+de\s+(\d{4})\s+a\s+las\s+([0-9]{2}:[0-9]{2}(?::[0-9]{2})?)/i
  );

  const etaMatch = etaIso || etaEs;

  const distMatch = text.match(
    /[-*•]?\s*Distancia\s+[Ff]altante\s*:?\s*([0-9][0-9,.]*)\s*(km|m)?\b/i
  );
  // handles "Llegará a Tiempo: Sí/No" and "Puntualidad: ✓/✗ ..."
  const onTimeMatch = text.match(
    /[-*•]?\s*(?:Llegar[aá]\s+a\s+Tiempo|Puntualidad)\s*:?\s*(.+?)(?:\n|$)/i
  );
  if (!etaMatch && !distMatch && !onTimeMatch) return null;

  let fecha = null;
  if (etaIso) {
    fecha = `${etaIso[1]} ${etaIso[2]}`;
  } else if (etaEs) {
    const day   = etaEs[1].padStart(2, '0');
    const month = String(MESES_ES[etaEs[2].toLowerCase()] ?? 1).padStart(2, '0');
    const year  = etaEs[3];
    const time  = etaEs[4];
    fecha = `${year}-${month}-${day} ${time}`;
  }

  let distanciaFaltante = null;
  if (distMatch) {
    const num  = parseFloat(distMatch[1].replace(/,/g, ''));
    const unit = (distMatch[2] ?? '').toLowerCase().trim();
    // normalize to meters so fmtDist can format correctly
    distanciaFaltante = String(unit === 'km' ? num * 1000 : num);
  }

  let llegaraATiempo = null;
  if (onTimeMatch) {
    const raw = onTimeMatch[1].trim();
    if (/si|sí|true|✓|a\s*tiempo|puntual/i.test(raw)) llegaraATiempo = 'Sí';
    else if (/no|false|✗|retraso|tarde/i.test(raw))    llegaraATiempo = 'No';
    else llegaraATiempo = raw;
  }

  return { fecha, distanciaFaltante, llegaraATiempo };
};

const calculateDistance = (coords) => {
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    const { lat: lat1, lng: lng1 } = coords[i - 1];
    const { lat: lat2, lng: lng2 } = coords[i];
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }
  return total;
};

const ETACard = ({ eta, distanciaFaltante, llegaraATiempo }) => {
  if (!eta && distanciaFaltante == null) return null;

  /* Parse datetime */
  let dateStr = '—', timeStr = '—', daysLeft = null;
  if (eta) {
    const d = new Date(eta.replace(' ', 'T'));
    if (!isNaN(d)) {
      dateStr = d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
      timeStr = d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
      const diffMs = d - Date.now();
      daysLeft = Math.ceil(diffMs / 86400000);
    }
  }

  /* Normalize on-time */
  const onTime = llegaraATiempo === true || llegaraATiempo === 1
    || String(llegaraATiempo).toLowerCase() === 'si'
    || String(llegaraATiempo).toLowerCase() === 'sí'
    || String(llegaraATiempo).toLowerCase() === 'true';
  const hasOnTime = llegaraATiempo != null && String(llegaraATiempo) !== '';

  /* Format distance */
  const fmtDist = (v) => {
    if (v == null) return '—';
    const n = parseFloat(v);
    if (isNaN(n)) return String(v);
    if (n >= 1000) return `${(n / 1000).toLocaleString('es-MX', { maximumFractionDigits: 1 })} km`;
    return `${n.toLocaleString('es-MX', { maximumFractionDigits: 0 })} m`;
  };

  return (
    <div className="eta-card">
      <div className="eta-card-header">
        <span className="eta-card-icon">🎯</span>
        <span className="eta-card-title">TIEMPO ESTIMADO DE LLEGADA</span>
        {daysLeft != null && (
          <span className={`eta-days-badge ${daysLeft < 0 ? 'overdue' : daysLeft === 0 ? 'today' : ''}`}>
            {daysLeft < 0 ? `${Math.abs(daysLeft)}d vencido` : daysLeft === 0 ? 'Hoy' : `en ${daysLeft}d`}
          </span>
        )}
      </div>
      <div className="eta-card-body">
        <div className="eta-datetime">
          <span className="eta-date">{dateStr}</span>
          <span className="eta-time">{timeStr}</span>
        </div>
        <div className="eta-stats">
          {distanciaFaltante != null && (
            <div className="eta-stat">
              <span className="eta-stat-icon">📍</span>
              <span className="eta-stat-label">Distancia faltante</span>
              <span className="eta-stat-value">{fmtDist(distanciaFaltante)}</span>
            </div>
          )}
          {hasOnTime && (
            <div className={`eta-stat eta-ontime ${onTime ? 'yes' : 'no'}`}>
              <span className="eta-stat-icon">{onTime ? '✅' : '⚠️'}</span>
              <span className="eta-stat-label">Llegará a tiempo</span>
              <span className="eta-stat-value">{onTime ? 'Sí' : 'No'}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Transformer Loader: robot → trailer (image-based) ── */
const TransformerLoader = () => (
  <div className="tf-loader">
    <div className="tf-spinner">
      <div className="tf-dot tf-dot-1"/>
      <div className="tf-dot tf-dot-2"/>
      <div className="tf-dot tf-dot-3"/>
    </div>
    <span className="tf-label">procesando...</span>
  </div>
);
const TrackingComponent = ({ coordinates, animated = false, currentPosition = null }) => {
  if (!coordinates?.length) return null;
  const gpsName = currentPosition?.nombre_gps ?? null;
  const battery = currentPosition?.bateria     ?? null;
  const signal  = currentPosition?.ultima_señal_gps ?? null;
  return (
    <div className="tracking-container">
      <div className="tracking-header">
        <MapPin size={13} />
        <span>
          {gpsName ? gpsName.toUpperCase() : 'TRACKING'}
          &nbsp;·&nbsp;{coordinates.length} pts
          &nbsp;·&nbsp;~{calculateDistance(coordinates).toFixed(2)} km
        </span>
        {battery != null && (
          <span className="tracking-header-chip">🔋 {battery}%</span>
        )}
        {signal && (
          <span className="tracking-header-chip">📡 {signal}</span>
        )}
      </div>
      <div className="tracking-map">
        <TrackingMap
          coordinates={coordinates}
          tamanio={{ width: '100%', height: '260px' }}
          animated={animated}
          currentPosition={currentPosition}
        />
      </div>
    </div>
  );
};

/* ── Chatbot Component ──────────────────────────────── */
const Chatbot = () => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const [conversationId, setConversationId] = useState('');
  const [, setTrackingCoordinates] = useState([]);
  const [hasNewMessage, setHasNewMessage] = useState(false);
  const [reportTasks, setReportTasks] = useState({});
  const [trackingMaps, setTrackingMaps] = useState({});
  const [positionMaps, setPositionMaps] = useState({});
  const [serviceTimelines, setServiceTimelines] = useState({});
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const progressReadersRef = useRef({});
  const floatBtnRef = useRef(null);
  const floatAnimsRef = useRef({});
  const chatCardRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const token = sessionStorage.getItem('tok');
  const user = JSON.parse(sessionStorage.getItem("usuario"));
  const sessionId = sessionStorage.getItem('chatbot-session-id') || `web_session_${user?.id ?? 'anon'}`;

  /* ── Session restore ── */
  useEffect(() => {
    const savedMessages       = sessionStorage.getItem('chatbot-messages');
    const savedConversationId = sessionStorage.getItem('chatbot-conversation-id');
    const savedChatState      = sessionStorage.getItem('chatbot-open');

    if (savedChatState === 'true') setIsOpen(true);

    if (savedMessages) {
      const parsed = JSON.parse(savedMessages).map(m => ({
        ...m,
        timestamp: new Date(m.timestamp)
      }));
      setMessages(parsed);
    } else {
      sendInitialMessage('Hola');
    }

    if (savedConversationId) setConversationId(savedConversationId);
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem('chatbot-messages',
        JSON.stringify(messages.map(m => ({ ...m, timestamp: m.timestamp.toISOString() }))));
    }
  }, [messages]);

  useEffect(() => {
    if (conversationId) sessionStorage.setItem('chatbot-conversation-id', conversationId);
  }, [conversationId]);

  useEffect(() => {
    sessionStorage.setItem('chatbot-open', isOpen.toString());
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 150);
  }, [isOpen]);

  useEffect(() => {
    if (isAutoScrollEnabled && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isTyping]);

  useEffect(() => {
    if (messages.length > 50) setMessages(prev => prev.slice(-50));
  }, [messages]);

  /* Cancelar SSE de progreso al desmontar */
  useEffect(() => {
    return () => {
      Object.values(progressReadersRef.current).forEach(r => {
        try { r.cancel(); } catch (_) {}
      });
    };
  }, []);

  /* ── Idle float animations ── */
  useEffect(() => {
    if (isOpen) return;
    const tid = setTimeout(() => {
      const btn = floatBtnRef.current;
      if (!btn) return;

      floatAnimsRef.current.float = animate(btn, {
        translateY: -8,
        duration: 2600,
        easing: 'easeInOutSine',
        loop: true,
        direction: 'alternate',
      });

      const orbitEl = btn.querySelector('.sa-orbit');
      if (orbitEl) {
        floatAnimsRef.current.orbit = animate(orbitEl, {
          opacity: 0.5,
          duration: 1900,
          easing: 'easeInOutSine',
          loop: true,
          direction: 'alternate',
          delay: 400,
        });
      }

      const planetEl = btn.querySelector('.sa-planet');
      if (planetEl) {
        floatAnimsRef.current.planet = animate(planetEl, {
          opacity: 0.6,
          duration: 1500,
          easing: 'easeInOutSine',
          loop: true,
          direction: 'alternate',
          delay: 200,
        });
      }
    }, 50);

    return () => {
      clearTimeout(tid);
      Object.values(floatAnimsRef.current).forEach(a => { try { a?.cancel?.(); } catch (_) {} });
      floatAnimsRef.current = {};
    };
  }, [isOpen]);

  /* ── Shake on new message ── */
  useEffect(() => {
    if (!hasNewMessage) return;
    const btn = floatBtnRef.current;
    if (!btn) return;

    floatAnimsRef.current.float?.pause?.();
    const tl = createTimeline({ onComplete: () => floatAnimsRef.current.float?.resume?.() });
    tl.add(btn, { translateX: -9, duration: 75, easing: 'linear' })
      .add(btn, { translateX:  9, duration: 75, easing: 'linear' })
      .add(btn, { translateX: -7, duration: 75, easing: 'linear' })
      .add(btn, { translateX:  7, duration: 75, easing: 'linear' })
      .add(btn, { translateX: -4, duration: 75, easing: 'linear' })
      .add(btn, { translateX:  0, duration: 120, easing: 'easeOutBack' });
  }, [hasNewMessage]);

  const handleScroll = () => {
    if (!chatContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    setIsAutoScrollEnabled(scrollHeight - scrollTop <= clientHeight + 60);
  };

  /* ── Float button interactions ── */
  const handleFloatMouseEnter = () => {
    if (!floatBtnRef.current) return;
    floatAnimsRef.current.float?.pause?.();
    animate(floatBtnRef.current, {
      translateY: -8,
      duration: 220,
      easing: 'easeOutQuad',
    });
  };

  const handleFloatMouseLeave = () => {
    if (!floatBtnRef.current) return;
    animate(floatBtnRef.current, {
      translateY: 0,
      duration: 450,
      easing: 'easeOutQuad',
      onComplete: () => floatAnimsRef.current.float?.resume?.(),
    });
  };

  const handleFloatClick = () => {
    if (!floatBtnRef.current) return;
    floatAnimsRef.current.float?.pause?.();
    animate(floatBtnRef.current, {
      translateY: 6,
      duration: 100,
      easing: 'easeInQuad',
      onComplete: () => {
        animate(floatBtnRef.current, {
          translateY: 0,
          duration: 380,
          easing: 'easeOutQuad',
          onComplete: () => {
            setIsOpen(true);
            setHasNewMessage(false);
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 120);
          },
        });
      },
    });
  };

  /* ── SSE streaming helper ── */
  const streamFromAPI = async (messageId, body) => {
    const response = await fetch(`${api_ia}stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);

    const reader  = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulated = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const lines = decoder.decode(value, { stream: true }).split('\n');
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const dataStr = line.slice(6).trim();
          if (!dataStr) continue;
          const data = JSON.parse(dataStr);

          console.log('[SSE]', data);

          switch (data.type) {
            case 'chunk':
              if (data.content) {
                accumulated += data.content;
                updateStreamingMessage(messageId, accumulated);
                await new Promise(r => setTimeout(r, 25));
              }
              break;
            case 'tool':
              if (data.content)
                updateStreamingMessage(messageId, accumulated + `\n\n🛠️ ${data.content}`);
              break;
            case 'error':
              throw new Error(data.message || data.error || 'Error desconocido');
            case 'report_started':
              if (data.task_id) {
                setMessages(prev => prev.map(m => m.id === messageId ? { ...m, hasReport: true } : m));
                subscribeToReportProgress(data.task_id, messageId);
              }
              break;
            case 'tracking_ready':
              console.log('[SSE] tracking_ready → fetchTrackingMap po=', data.po, data.fecha_inicio, data.fecha_fin, data.fecha, data.solo_detenciones);
              setMessages(prev => prev.map(m => m.id === messageId ? { ...m, mapType: 'tracking_ready' } : m));
              fetchTrackingMap(data.po, messageId, data.fecha_inicio ?? data.fecha, data.fecha_fin, data.solo_detenciones);
              break;
            case 'position_ready':
              console.log('[SSE] position_ready → fetchPosition po=', data.po);
              setMessages(prev => prev.map(m => m.id === messageId ? { ...m, mapType: 'position_ready' } : m));
              fetchPosition(data.po, messageId);
              break;
            case 'samsung_timeline_ready':
              console.log('[SSE] samsung_timeline_ready → fetchServiceTimeline po=', data.po);
              setMessages(prev => prev.map(m => m.id === messageId ? { ...m, mapType: 'samsung_timeline_ready' } : m));
              fetchServiceTimeline(data.po, messageId);
              break;
            case 'done':
              finalizeStreamingMessage(messageId);
              return;
            default:
              break;
          }
        } catch (e) {
          console.warn('[SSE] parse/handler error:', e);
        }
      }
    }
  };

  const sendInitialMessage = async (message) => {
    setIsTyping(true);
    const messageId = Date.now();
    setMessages([{ id: messageId, text: '', sender: 'bot', timestamp: new Date(), isStreaming: true }]);
    try {
      await streamFromAPI(messageId, {
        prompt: message,
        user_id: user.id,
        name_user: user.username,
        session_id: sessionId
      });
    } catch (err) {
      updateStreamingMessage(messageId, `❌ ${err.message}`);
    } finally {
      finalizeStreamingMessage(messageId);
      setIsTyping(false);
    }
  };

  const updateStreamingMessage = (messageId, text) => {
    const coords = detectCoordinates(text);
    if (coords?.length) {
      setTrackingCoordinates(prev => {
        const fresh = coords.filter(nc =>
          !prev.some(ec =>
            Math.abs(ec.lat - nc.lat) < 0.000001 &&
            Math.abs(ec.lng - nc.lng) < 0.000001));
        return [...prev, ...fresh];
      });
    }
    setMessages(prev =>
      prev.map(m => m.id === messageId ? { ...m, text: formatText(text) } : m));
  };

  const finalizeStreamingMessage = (messageId) => {
    setMessages(prev =>
      prev.map(m => m.id === messageId ? { ...m, isStreaming: false } : m));
    if (!isOpen) setHasNewMessage(true);
  };

  const handleSendMessage = async () => {
    if (!inputText.trim()) return;
    const currentInput = inputText;
    setInputText('');
    setIsAutoScrollEnabled(true);
    setIsTyping(true);

    const userId    = Date.now();
    const messageId = userId + 1;

    setMessages(prev => [
      ...prev,
      { id: userId,    text: currentInput, sender: 'user', timestamp: new Date() },
      { id: messageId, text: '',           sender: 'bot',  timestamp: new Date(), isStreaming: true }
    ]);

    try {
      await streamFromAPI(messageId, {
        prompt:          currentInput,
        user_id:         user.id,
        name_user:       user.username,
        session_id:      'web_session',
        conversation_id: conversationId
      });
    } catch (err) {
      updateStreamingMessage(messageId, `❌ ${err.message}`);
    } finally {
      finalizeStreamingMessage(messageId);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  /* ── Report progress SSE ── */
  const subscribeToReportProgress = async (taskId, messageId) => {
    setReportTasks(prev => ({
      ...prev,
      [taskId]: { progress: 0, message: 'Preparando reporte...', status: 'running', fileUrl: null, messageId }
    }));

    let reader;
    try {
      const response = await fetch(`${api_ia}api/report-progress/${taskId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      reader = response.body.getReader();
      progressReadersRef.current[taskId] = reader;
      const decoder = new TextDecoder();

      outer: while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const lines = decoder.decode(value, { stream: true }).split('\n');
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const dataStr = line.slice(6).trim();
            if (!dataStr) continue;
            const data = JSON.parse(dataStr);

            if (data.type === 'progress') {
              setReportTasks(prev => ({
                ...prev,
                [taskId]: {
                  ...prev[taskId],
                  progress: data.progress ?? prev[taskId]?.progress ?? 0,
                  message:  data.message  ?? prev[taskId]?.message  ?? ''
                }
              }));

              if (data.status === 'completed' && data.file_url) {
                setReportTasks(prev => ({
                  ...prev,
                  [taskId]: { ...prev[taskId], progress: 100, status: 'completed', fileUrl: data.file_url }
                }));
                break outer;
              }
              if (data.status === 'error') {
                throw new Error(data.message || 'Error al generar el reporte');
              }
            }
          } catch (parseErr) {
            console.warn('Report progress parse error:', parseErr);
          }
        }
      }
    } catch (err) {
      setReportTasks(prev => ({
        ...prev,
        [taskId]: { ...(prev[taskId] || { messageId }), status: 'error', message: err.message }
      }));
    } finally {
      delete progressReadersRef.current[taskId];
    }
  };

  /* ── Tracking map fetch (triggered by tracking_ready SSE event) ── */
  const fetchTrackingMap = async (po, messageId, fechaInicio, fechaFin, soloDetenciones) => {
    setTrackingMaps(prev => ({ ...prev, [messageId]: { po, coordinates: null, status: 'loading' } }));
    try {
      const params = new URLSearchParams();
      if (fechaInicio) {
        params.set('fecha_inicio', fechaInicio);
        if (fechaFin) params.set('fecha_fin', fechaFin);
      }
      if (soloDetenciones === true) params.set('solo_detenciones', 'true');
      const qs = params.toString() ? `?${params}` : '';
      const res = await fetch(`${api_ia}api/tracking-map/${po}${qs}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      console.log('[trackingMap] respuesta API:', json);

      let coordinates = [];

      const parseHistorialPuntos = (puntos) => {
        const seen = new Set();
        const result = [];
        puntos.forEach(p => {
          if (!p.valid) return;
          const lat = parseFloat(p.position?.latitude);
          const lng = parseFloat(p.position?.longitude);
          if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return;
          const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
          if (seen.has(key)) return;
          seen.add(key);
          result.push({
            lat,
            lng,
            speed:   p.velocity?.groundSpeed ?? p.variables?.speed ?? 0,
            battery: p.variables?.batteryLevel ?? null,
            utc:     p.utc ?? null,
            heading: p.velocity?.heading ?? 0,
          });
        });
        return result;
      };

      if (Array.isArray(json.historial_puntos) && json.historial_puntos.length > 0) {
        coordinates = parseHistorialPuntos(json.historial_puntos);
      } else if (Array.isArray(json.puntos) && json.puntos.length > 0) {
        coordinates = parseHistorialPuntos(json.puntos);
      } else if (Array.isArray(json.coordenadas) && json.coordenadas.length > 0) {
        coordinates = json.coordenadas.map(c => ({
          lat: parseFloat(c.lat ?? c.latitude ?? c.latitud),
          lng: parseFloat(c.lng ?? c.lon ?? c.longitude ?? c.longitud),
          speed: 0, battery: null, utc: null, heading: 0,
        })).filter(c => !isNaN(c.lat) && !isNaN(c.lng));

      } else if (Array.isArray(json.tracking_completo) && json.tracking_completo.length > 0) {
        const seen = new Set();
        const addPoint = (pos) => {
          if (!pos) return;
          const lat = parseFloat(pos.latitude);
          const lng = parseFloat(pos.longitude);
          if (isNaN(lat) || isNaN(lng)) return;
          const key = `${lat.toFixed(6)},${lng.toFixed(6)}`;
          if (!seen.has(key)) { seen.add(key); coordinates.push({ lat, lng, speed: 0, battery: null, utc: null, heading: 0 }); }
        };
        const segments = json.tracking_completo;
        segments.forEach((seg, i) => {
          addPoint(seg.startTrackPoint?.position);
          if (i === segments.length - 1) addPoint(seg.endTrackPoint?.position);
        });
      }
      const eta = {
        fecha:             json.eta               ?? json.fecha_eta             ?? null,
        distanciaFaltante: json.distancia_faltante ?? json.distancia_restante    ?? null,
        llegaraATiempo:    json.llegara_a_tiempo   ?? json.llega_a_tiempo        ?? null,
      };
      const posicionActual = json.posicion_actual ?? null;
      setTrackingMaps(prev => ({ ...prev, [messageId]: { po, coordinates, eta, posicionActual, status: 'ready' } }));
      if (coordinates.length > 0 && chatCardRef.current && !isExpanded) {
        setIsExpanded(true);
        animate(chatCardRef.current, {
          width: Math.min(700, window.innerWidth * 0.9),
          duration: 520,
          easing: 'easeOutQuart',
        });
      }
    } catch (err) {
      setTrackingMaps(prev => ({ ...prev, [messageId]: { po, coordinates: null, status: 'error', error: err.message } }));
    }
  };

  /* ── Position fetch (triggered by position_ready SSE event) ── */
  const fetchPosition = async (po, messageId) => {
    setPositionMaps(prev => ({ ...prev, [messageId]: { po, data: null, status: 'loading' } }));
    try {
      const res = await fetch(`${api_ia}api/position/${po}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      console.log('[positionMap] respuesta API:', data);
      setPositionMaps(prev => ({ ...prev, [messageId]: { po, data, status: 'ready' } }));
      if (chatCardRef.current && !isExpanded) {
        setIsExpanded(true);
        animate(chatCardRef.current, {
          width: Math.min(700, window.innerWidth * 0.9),
          duration: 520,
          easing: 'easeOutQuart',
        });
      }
    } catch (err) {
      setPositionMaps(prev => ({ ...prev, [messageId]: { po, data: null, status: 'error', error: err.message } }));
    }
  };

  /* ── Service timeline fetch (triggered by samsung_timeline_ready SSE event) ── */
  const fetchServiceTimeline = async (po, messageId) => {
    setServiceTimelines(prev => ({ ...prev, [messageId]: { po, data: null, status: 'loading' } }));
    try {
      const res = await fetch(`${api_ia}api/servicio/${po}/eventos-samsung`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      console.log('[serviceTimeline] respuesta API:', json);
      setServiceTimelines(prev => ({ ...prev, [messageId]: { po, data: json, status: 'ready' } }));
    } catch (err) {
      setServiceTimelines(prev => ({ ...prev, [messageId]: { po, data: null, status: 'error', error: err.message } }));
    }
  };

  const collapseCard = () => {
    if (!isExpanded || !chatCardRef.current) return;
    setIsExpanded(false);
    animate(chatCardRef.current, {
      width: Math.min(420, window.innerWidth * 0.92),
      duration: 420,
      easing: 'easeOutQuart',
    });
  };

  /* ── Helpers ── */
  const formatTime = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatText = (text) => {
    if (!text) return text;
    return text
      .replace(/\t/g, '    ')
      .replace(/\n\s*\n/g, '\n\n')
      .replace(/^\s+/gm, m => ' '.repeat(m.length))
      .split('\n')
      .map(l => l.trim() === '' ? ' ' : l)
      .join('\n');
  };


  const renderTextWithLinks = (text) => {
    if (!text) return null;
    const parts = text.split(/(https?:\/\/[^\s]+)/g);
    return parts.map((part, i) =>
      /^https?:\/\//.test(part)
        ? <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="skybot-map-link">{part}</a>
        : part
    );
  };

  const detectCoordinates = (text) => {
    const patterns = [
      /(?:latitud|latitude|lat)\s*:?\s*(-?\d+\.\d+)\s*,?\s*(?:longitud|longitude|lng|lon)\s*:?\s*(-?\d+\.\d+)/gi,
      /(?:latitud|latitude|lat)\s*:?\s*(-?\d+\.\d+)\s*[\n\r]?\s*(?:longitud|longitude|lng|lon)\s*:?\s*(-?\d+\.\d+)/gi,
      /\(\s*(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)\s*\)/g,
      /(-?\d{1,2}\.\d{6})\s*,\s*(-?\d{1,3}\.\d{6})/g,
      /(-?\d{1,2}\.\d{3,})\s*,\s*(-?\d{1,3}\.\d{3,})/g
    ];
    const all = [];
    for (const p of patterns) {
      for (const m of [...text.matchAll(p)]) {
        const lat = parseFloat(m[1]), lng = parseFloat(m[2]);
        if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) all.push({ lat, lng });
      }
    }
    return all.length > 0 ? all : null;
  };


  /* ── Report progress card ── */
  const ReportProgressCard = ({ task }) => {
    if (task.status === 'completed') {
      return (
        <div className="skybot-report-card skybot-report-done">
          <div className="skybot-report-done-icon">📊</div>
          <div className="skybot-report-done-body">
            <span className="skybot-report-done-label">Reporte listo</span>
            <a href={task.fileUrl} download target="_blank" rel="noopener noreferrer"
               className="skybot-download-link">
              📥 Descargar reporte
            </a>
          </div>
        </div>
      );
    }
    if (task.status === 'error') {
      return (
        <div className="skybot-report-card skybot-report-error-card">
          <span>⚠️ {task.message || 'Error al generar el reporte'}</span>
        </div>
      );
    }
    return (
      <div className="skybot-report-card skybot-report-running">
        <div className="skybot-report-top">
          <div className="skybot-report-spinner" />
          <span className="skybot-report-title">Generando reporte</span>
          <span className="skybot-report-pct">{task.progress}%</span>
        </div>
        <div className="skybot-progress-track">
          <div className="skybot-progress-fill" style={{ width: `${task.progress}%` }} />
        </div>
        {task.message && <span className="skybot-report-msg">{task.message}</span>}
      </div>
    );
  };

  /* ── Floating button (portal) ── */
  if (!isOpen) {
    return createPortal(
      <div className="skybot-float-wrapper">
        <div
          ref={floatBtnRef}
          className={`skybot-float-btn${hasNewMessage ? ' has-notification' : ''}`}
          onClick={handleFloatClick}
          onMouseEnter={handleFloatMouseEnter}
          onMouseLeave={handleFloatMouseLeave}
        >
          <TfLogo size={110} animated />
          {hasNewMessage && <div className="skybot-float-badge">!</div>}
        </div>
      </div>,
      document.body
    );
  }

  /* ── Chat panel ── */
  return (
    <div className="skybot-chatbot-main">
      <div className="skybot-chatbot-card" ref={chatCardRef}>

        {/* Header */}
        <div className="skybot-header">
          <div className="skybot-header-left">
            <div className="skybot-avatar">
              <TfLogo size={44} animated />
            </div>
            <div className="skybot-header-info">
              <h3>SK<span>IA</span>ngel</h3>
              <div className="skybot-header-status">
                <div className={`skybot-status-dot${isTyping ? ' thinking' : ''}`} />
                <span style={{ fontFamily:'var(--sky-mono)', fontSize:'11px' }}>
                  {isTyping ? 'procesando...' : 'en línea'}
                </span>
              </div>
            </div>
          </div>

          <div className="skybot-header-actions">
            <button
              onClick={() => { setIsOpen(false); setHasNewMessage(false); }}
              className="skybot-close-btn"
              title="Cerrar"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Messages */}
        <div
          ref={chatContainerRef}
          onScroll={handleScroll}
          className="skybot-chat-container"
        >
          {!isAutoScrollEnabled && (
            <div className="skybot-scroll-indicator">
              <button
                onClick={() => {
                  setIsAutoScrollEnabled(true);
                  messagesEndRef.current?.scrollIntoView({ behavior:'smooth' });
                }}
                className="skybot-scroll-btn"
              >
                ↓ Nuevos mensajes
              </button>
            </div>
          )}

          <div className="skybot-messages">
            {messages.map(message => (
              <div
                key={message.id}
                className={`skybot-message skybot-${message.sender}`}
              >
                {message.sender === 'bot' && (
                  <div className="skybot-message-avatar">
                    <TfLogo size={34} animated={false} />
                  </div>
                )}

                <div className="skybot-message-bubble">
                  {message.isStreaming && !message.text ? (
                    <TransformerLoader />
                  ) : !message.hasReport && (
                    <p className="skybot-message-text">
                      {message.mapType
                        ? renderTextWithLinks(message.text)
                        : message.text}
                    </p>
                  )}

                  {/* ── Post-stream content ── */}
                  {message.sender === 'bot' && !message.isStreaming && (() => {
                    const trackMap      = trackingMaps[message.id];
                    const posMap        = positionMaps[message.id];
                    const svcTimeline   = serviceTimelines[message.id];
                    const etaInfo       = detectETA(message.text);
                    const textCoords = detectCoordinates(message.text);
                    const tasks      = Object.entries(reportTasks)
                      .filter(([, t]) => t.messageId === message.id);
                    return (
                      <>
                        {/* ETA card — parsed from AI text response */}
                        {etaInfo && (
                          <ETACard
                            eta={etaInfo.fecha}
                            distanciaFaltante={etaInfo.distanciaFaltante}
                            llegaraATiempo={etaInfo.llegaraATiempo}
                          />
                        )}

                        {/* Position pin — only when there is NO full tracking route */}
                        {posMap && !trackMap && (
                          <div className="skybot-coordinates-maps">
                            {posMap.status === 'loading' && (
                              <div className="skybot-tracking-loading">
                                <div className="skybot-report-spinner" />
                                <span>Cargando posición de {posMap.po}…</span>
                              </div>
                            )}
                            {posMap.status === 'ready' && (
                              <div className="tracking-container">
                                <div className="tracking-header">
                                  <span>📍 POSICIÓN ACTUAL — {posMap.po.toUpperCase()}</span>
                                </div>
                                <PositionMap data={posMap.data} tamanio={{ width: '100%', height: '240px' }} />
                              </div>
                            )}
                            {posMap.status === 'error' && (
                              <div className="skybot-tracking-error">
                                ⚠️ No se pudo obtener la posición ({posMap.error})
                              </div>
                            )}
                          </div>
                        )}

                        {/* Full tracking route */}
                        {trackMap && (
                          <div className="skybot-coordinates-maps">
                            {trackMap.status === 'loading' && (
                              <div className="skybot-tracking-loading">
                                <div className="skybot-report-spinner" />
                                <span>Cargando ruta de {trackMap.po}…</span>
                              </div>
                            )}
                            {trackMap.status === 'ready' && (
                              trackMap.coordinates?.length > 0
                                ? <TrackingComponent
                                    coordinates={trackMap.coordinates}
                                    animated
                                    currentPosition={trackMap.posicionActual}
                                  />
                                : <div className="skybot-tracking-error">⚠️ La ruta no tiene coordenadas</div>
                            )}
                            {trackMap.status === 'error' && (
                              <div className="skybot-tracking-error">
                                ⚠️ No se pudo cargar la ruta ({trackMap.error})
                              </div>
                            )}
                          </div>
                        )}

                        {/* Service timeline — triggered by samsung_timeline_ready */}
                        {svcTimeline && (
                          <div className="skybot-coordinates-maps">
                            {svcTimeline.status === 'loading' && (
                              <ServiceTimelineSkeleton po={svcTimeline.po} />
                            )}
                            {svcTimeline.status === 'ready' && (
                              <ServiceTimeline
                                po={svcTimeline.data.po ?? svcTimeline.po}
                                plataforma={svcTimeline.data.plataforma}
                                eventosObj={svcTimeline.data.eventos ?? {}}
                              />
                            )}
                            {svcTimeline.status === 'error' && (
                              <div className="skybot-tracking-error">
                                ⚠️ No se pudo cargar el timeline ({svcTimeline.error})
                              </div>
                            )}
                          </div>
                        )}

                        {tasks.map(([taskId, task]) => (
                          <div key={taskId} className="skybot-report-wrapper">
                            <ReportProgressCard task={task} />
                          </div>
                        ))}
                        {/* Text-detected coords only when no SSE map exists */}
                        {!trackMap && !posMap && textCoords && (
                          <div className="skybot-coordinates-maps">
                            <TrackingComponent coordinates={textCoords} />
                          </div>
                        )}
                      </>
                    );
                  })()}

                  <span className="skybot-message-time">{formatTime(message.timestamp)}</span>
                </div>

                {message.sender === 'user' && (
                  <div className="skybot-message-avatar skybot-user-avatar">
                    TÚ
                  </div>
                )}
              </div>
            ))}
          </div>
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="skybot-input-section">
          <div className="skybot-input-container">
            <input
              ref={inputRef}
              type="text"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              onFocus={collapseCard}
              onKeyDown={handleKeyPress}
              placeholder="Consulta sobre logística, rutas, tractos..."
              className="skybot-message-input"
              disabled={isTyping}
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputText.trim() || isTyping}
              className="skybot-send-btn"
              title="Enviar"
            >
              <Send size={15} />
            </button>
          </div>
          <p className="skybot-powered-by">Powered by <span>SKIAngel</span></p>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
